import { Component, Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { I18nProvider, useI18n } from "./i18n/I18nProvider";
import { AuthProvider } from "./context/AuthContext";
import AuthCallback from "./context/AuthCallback";
import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";
import { Loading } from "./components/common/States";
import { ASSETS } from "./lib/assets";
import { track } from "./lib/analytics";
import Home from "./pages/Home";

const P = (f) => lazy(f);
const World = P(() => import("./pages/World"));
const Learn = P(() => import("./pages/Learn"));
const LessonDetail = P(() => import("./pages/LessonDetail"));
const Dashboard = P(() => import("./pages/Dashboard"));
const Library = P(() => import("./pages/Library"));
const ResourceDetail = P(() => import("./pages/ResourceDetail"));
const AiTutor = P(() => import("./pages/AiTutor"));
const Community = P(() => import("./pages/Community"));
const Contribute = P(() => import("./pages/Contribute"));
const Impact = P(() => import("./pages/Impact"));
const About = P(() => import("./pages/About"));
const Token = P(() => import("./pages/Token"));
const Docs = P(() => import("./pages/Docs"));
const Faq = P(() => import("./pages/Faq"));
const Contact = P(() => import("./pages/Contact"));
const Legal = P(() => import("./pages/Legal"));
const Login = P(() => import("./pages/Login"));
const Settings = P(() => import("./pages/Settings"));
const Admin = P(() => import("./pages/Admin"));
const NotFound = P(() => import("./pages/NotFound"));

function ErrorFallback() {
  const { t } = useI18n();
  return (
    <div data-testid="error-boundary" className="mx-auto flex max-w-xl flex-col items-center gap-4 px-6 py-24 text-center">
      <img src={ASSETS.explorer.src} alt="" width={160} height={160} className="h-40 w-40 object-contain" />
      <h1 className="font-heading text-3xl font-black text-[#17345F]">{t("notFound.errTitle")}</h1>
      <p className="text-[#17345F]/70">{t("notFound.errBody")}</p>
      <div className="flex gap-3">
        <button data-testid="error-reload-btn" onClick={() => window.location.reload()} className="btn-pill btn-primary">{t("common.retry")}</button>
        <a href="/" data-testid="error-boundary-home-link" className="btn-pill btn-ghost">{t("common.home")}</a>
      </div>
    </div>
  );
}

class ErrorBoundary extends Component {
  state = { err: null };
  static getDerivedStateFromError(err) { return { err }; }
  render() { return this.state.err ? <ErrorFallback /> : this.props.children; }
}

function ScrollAndTrack() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView({ behavior: "smooth" }); return; }
    }
    window.scrollTo(0, 0);
    track("page_view", { path: pathname });
  }, [pathname, hash]);
  return null;
}

function OfflineBanner() {
  const { t } = useI18n();
  return <div data-testid="offline-banner" role="status" className="hidden bg-amber-100 px-4 py-2 text-center text-sm font-semibold text-amber-900 [.is-offline_&]:block">{t("common.offline")}</div>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <ScrollAndTrack />
      <OfflineBanner />
      <Header />
      <main id="main" className="min-h-[60vh]">
        <ErrorBoundary key={location.pathname}>
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/world" element={<World />} />
              <Route path="/learn" element={<Learn />} />
              <Route path="/learn/:slug" element={<LessonDetail />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/library" element={<Library />} />
              <Route path="/library/:slug" element={<ResourceDetail />} />
              <Route path="/ai-tutor" element={<AiTutor />} />
              <Route path="/community" element={<Community />} />
              <Route path="/contribute" element={<Contribute />} />
              <Route path="/impact" element={<Impact />} />
              <Route path="/about" element={<About />} />
              <Route path="/token" element={<Token />} />
              <Route path="/docs" element={<Docs />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Legal doc="privacy" />} />
              <Route path="/terms" element={<Legal doc="terms" />} />
              <Route path="/accessibility" element={<Legal doc="accessibility" />} />
              <Route path="/guardians" element={<Legal doc="guardians" />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Login signup />} />
              <Route path="/settings" element={<Settings tab="account" />} />
              <Route path="/settings/privacy" element={<Settings tab="privacy" />} />
              <Route path="/settings/language" element={<Settings tab="language" />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
      <Footer />
    </>
  );
}

function useOnlineClass() {
  useEffect(() => {
    const set = () => document.documentElement.classList.toggle("is-offline", !navigator.onLine);
    set();
    window.addEventListener("online", set);
    window.addEventListener("offline", set);
    return () => { window.removeEventListener("online", set); window.removeEventListener("offline", set); };
  }, []);
}

export default function App() {
  useOnlineClass();
  return (
    <I18nProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </I18nProvider>
  );
}

export { Link };
